import { useEffect, useState } from 'react';
import { IconExternalLink, IconGift } from '@tabler/icons-react';
import { CONTEST_SITE_URL } from '../../config/contestSite';
import { useTranslation } from '../../i18n/hooks/useTranslation';
import ContestCountdown from './ContestCountdown';
import { useNumFmt } from './contestUi';

// Top banner of the contest page: status, name, dates, how much of the contest time has passed, and
// the countdown (or, once the time is over, where the contest stands).

const DAY = 24 * 60 * 60 * 1000;

const STATUS = {
    live: { chip: 'bg-emerald-500/15 text-emerald-300', dot: 'bg-emerald-400', ping: true, key: 'contest.hero.status.live' },
    upcoming: { chip: 'bg-amber-400/15 text-amber-200', dot: 'bg-amber-300', ping: false, key: 'contest.hero.status.upcoming' },
    pending: { chip: 'bg-amber-400/15 text-amber-200', dot: 'bg-amber-300', ping: false, key: 'contest.page.countdown.pending' },
    completed: { chip: 'bg-white/10 text-zinc-200', dot: 'bg-zinc-300', ping: false, key: 'contest.page.countdown.completed' },
};

export default function ContestHero({ contest, phase, winnerCount = 50, onElapsed }) {
    const { t, currentLanguage } = useTranslation();
    const fmt = useNumFmt();
    const status = STATUS[phase] || STATUS.live;

    // Progress only needs minute precision; the countdown ticks on its own every second.
    const [now, setNow] = useState(() => Date.now());
    useEffect(() => {
        const id = setInterval(() => setNow(Date.now()), 30 * 1000);
        return () => clearInterval(id);
    }, []);

    const start = new Date(contest.startDate).getTime();
    const end = new Date(contest.endDate).getTime();
    const totalDays = Math.max(1, Math.round((end - start) / DAY));
    const elapsed = Math.min(1, Math.max(0, (now - start) / (end - start)));
    const day = Math.min(totalDays, Math.max(1, Math.floor((now - start) / DAY) + 1));

    const locale = currentLanguage === 'bn' ? 'bn-BD' : 'en-US';
    const dateText = (iso, withYear) => new Date(iso).toLocaleDateString(locale, { day: 'numeric', month: 'short', ...(withYear ? { year: 'numeric' } : {}), timeZone: 'Asia/Dhaka' });

    return (
        <section className="relative flex flex-wrap items-start justify-between gap-7 overflow-hidden rounded-[22px] bg-zinc-900 p-5 text-white sm:p-7 dark:bg-zinc-950 dark:ring-1 dark:ring-zinc-800">
            <div className="flex min-w-0 flex-[1_1_380px] flex-col gap-3">
                <div className="flex flex-wrap items-center gap-2.5">
                    <span className={`inline-flex items-center gap-[7px] rounded-full px-[11px] py-[3px] text-[13px] font-semibold ${status.chip}`}>
                        <span className="relative flex h-[7px] w-[7px]">
                            {status.ping && <span className={`absolute inline-flex h-full w-full animate-ping rounded-full opacity-75 ${status.dot}`} />}
                            <span className={`relative inline-flex h-[7px] w-[7px] rounded-full ${status.dot}`} />
                        </span>
                        {t(status.key)}
                    </span>
                    <span className="text-sm text-zinc-400">{dateText(contest.startDate)} – {dateText(contest.endDate, true)}</span>
                </div>

                <h1 className="font-baloo text-[28px] font-bold leading-[1.2] tracking-tight text-white sm:text-[34px]">{contest.name}</h1>
                <div className="max-w-[720px] text-[15px] text-zinc-300">{t('contest.hero.tagline', { n: fmt(winnerCount) })}</div>

                {phase === 'live' && (
                    <div className="mt-1.5 flex max-w-[560px] flex-col gap-[7px]">
                        <div className="flex justify-between gap-3 text-[13px] text-zinc-400">
                            <span>{t('contest.hero.day', { d: fmt(day), total: fmt(totalDays) })}</span>
                            <span>{t('contest.hero.elapsed', { n: fmt(Math.round(elapsed * 100)) })}</span>
                        </div>
                        <div className="h-1.5 overflow-hidden rounded-full bg-white/10">
                            <div className="h-full rounded-full bg-primary transition-[width] duration-700 ease-out" style={{ width: `${(elapsed * 100).toFixed(1)}%` }} />
                        </div>
                    </div>
                )}
            </div>

            <div className="flex max-w-full flex-col items-start gap-4 lg:items-end">
                {/* Full details + prizes live on the contest site */}
                <a
                    href={CONTEST_SITE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex w-fit max-w-full items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-white/20 hover:text-white"
                >
                    <IconGift size={16} className="flex-shrink-0 text-primary" />
                    <span className="min-w-0">{t('contest.hero.details')}</span>
                    <IconExternalLink size={14} className="flex-shrink-0 opacity-70" />
                </a>
                <ContestCountdown contest={contest} phase={phase} onElapsed={onElapsed} />
            </div>
        </section>
    );
}
